// The owner's touch-up picker (detect-and-select, CREDITS_FLOW_DESIGN v3 §4).
//
// The photo review already names which photos could improve and how; this
// shows that grid with the findings as chips, lets the agent check photos,
// and prices only what is checked: "Touch up 3 photos · 45 credits". "Select
// all" is a small text link, never the primary action. A redo of a photo is
// free once, then 15. Photos that cannot be improved are refunded one by one;
// results show the confirmed credit/refund or free-redo settlement for each run.
//
// Mounted from the "Add to this listing" section (editor + share page): the
// candidates and the touch-up POST are keyed by the computation, and the
// owner gallery includes failed attempts as well as usable before/after photos.

import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ConfirmSpendSheet from "../../components/Credits/ConfirmSpendSheet";
import CreditsPaywallSheet from "../../components/Credits/CreditsPaywallSheet";
import { API_URL } from "../../constants";
import {
  ACTION_COSTS,
  formatCredits,
  formatCreditsNumber,
  insufficientCreditsFrom,
} from "../../constants/credits";
import usePaidAttempt from "../../hooks/usePaidAttempt";
import usePollReportDocument from "../../hooks/usePollReportDocument";
import { track } from "../../utils/analytics";
import { authHeader, getToken } from "../../utils/auth";
import {
  paidMediaSettlementKnown,
  paidMediaSettlementMessage,
} from "../../utils/paidMediaSettlement";
import { cleanPhotoName, photoUrlFromManifest } from "../../utils/photos";
import { touchupFailureMessage, touchupRunSummary } from "./touchupResults";

/**
 * @typedef {{
 *   photo: string,
 *   reasons: string[],
 *   already_touched: boolean,
 *   free_redo_available: boolean,
 *   cost: number,
 * }} TouchupCandidate
 */

/** Touch-ups usually land well inside this; say so rather than spin forever. */
const TOUCHUP_POLL_MAX_MS = 20 * 60 * 1000;

/** @type {Record<string, string>} */
const REASON_LABELS = {
  declutter: "Declutter",
  floor_scuff: "Floor marks",
  remove_outdoor_debris: "Yard debris",
  green_up_grass: "Green up the grass",
  sunny_day: "Brighten to sunny",
  brighten_room: "Brighten the room",
  touch_up_landscaping: "Tidy the landscaping",
};

/** Reason slugs are open-ended; unknown ones still read as words. */
export const reasonLabel = (/** @type {string} */ slug) => {
  if (REASON_LABELS[slug]) return REASON_LABELS[slug];
  const words = (slug || "").replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : slug;
};

/** @param {unknown} raw */
const normalizeCandidates = (raw) => {
  if (!Array.isArray(raw)) return /** @type {TouchupCandidate[]} */ ([]);
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object" || typeof item.photo !== "string" || !item.photo) {
      return [];
    }
    const cost = Number(item.cost);
    return [
      {
        photo: item.photo,
        reasons: Array.isArray(item.reasons)
          ? item.reasons.filter((/** @type {unknown} */ r) => typeof r === "string" && r)
          : [],
        already_touched: item.already_touched === true,
        free_redo_available: item.free_redo_available === true,
        cost: Number.isFinite(cost) && cost >= 0 ? cost : ACTION_COSTS.photo_touchup,
      },
    ];
  });
};

/**
 * The button's label for a selection: "Touch up 3 photos · 45 credits",
 * "Touch up 1 photo · your free redo", "Touch up 3 photos · 30 credits · 1 free redo".
 * @param {TouchupCandidate[]} selected
 */
export const touchupButtonLabel = (selected) => {
  const count = selected.length;
  if (count === 0) return "Touch up photos";
  const total = selected.reduce((sum, item) => sum + item.cost, 0);
  const freeCount = selected.filter((item) => item.cost === 0).length;
  const noun = count === 1 ? "photo" : "photos";
  if (total === 0) return `Touch up ${count} ${noun} · your free redo`;
  const free = freeCount > 0 ? ` · ${freeCount} free redo` : "";
  return `Touch up ${count} ${noun} · ${formatCredits(total)}${free}`;
};

const Panel = styled.div`
  margin-top: 10px;
  padding: 12px 0 0;
  border-top: 1px solid var(--brand-border, #e3ebec);
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const Intro = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--brand-ink-3, #5c7074);
  line-height: 1.45;
`;

const TextLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--brand-primary-700, #085552);
  cursor: pointer;
  text-decoration: underline;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin: 0 0 12px;
`;

const PhotoChoice = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid ${(/** @type {{ $checked?: boolean }} */ p) => (p.$checked ? "var(--brand-primary, #0e7e7a)" : "var(--brand-border, #e3ebec)")};
  border-radius: 10px;
  background: ${(/** @type {{ $checked?: boolean }} */ p) => (p.$checked ? "var(--brand-primary-50, #e6f3f2)" : "#ffffff")};
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--brand-ink, #0b1c1e);

  input {
    width: 16px;
    height: 16px;
    margin: 0;
  }
`;

const Thumb = styled.div`
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ChoiceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  word-break: break-all;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Chip = styled.span`
  font-size: 0.72rem;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 999px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid var(--brand-primary, #0e7e7a);
  background: var(--brand-primary, #0e7e7a);
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #0b605d;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid var(--brand-border-strong, #c9d6d8);
  background: #ffffff;
  color: var(--brand-ink-2, #2b3f42);
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Notice = styled.p`
  margin: 0 0 10px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => (p.$kind === "error" ? "#b91c1c" : "var(--brand-ink-2, #2b3f42)")};

  a {
    color: var(--brand-primary-700, #085552);
    font-weight: 600;
    text-decoration: underline;
  }
`;

/**
 * @param {{
 *   uuid: string,
 *   doc: Record<string, any>,
 *   balance: number | null,
 *   onSpendOpen?: () => unknown,
 *   onSpent?: () => unknown,
 *   onDocument: (fresh: Record<string, any>) => void,
 *   onClose: () => void,
 *   galleryLink?: (path: string) => React.ReactNode,
 *   resume?: { photos: string[], balance: number | null },
 * }} props
 *   balance: the account balance for the confirm line (null = unknown, "—").
 *   onSpendOpen: called when the confirm opens (the caller refreshes the balance).
 *   onSpent: called after a debit went through.
 *   galleryLink: renders the link to the finished before/after gallery.
 *   resume: back from a pack bought at a blocked "Touch up" tap — the same
 *   photos re-checked and the confirm reopened with one fact line.
 */
const TouchupPicker = ({
  uuid,
  doc,
  balance,
  onSpendOpen,
  onSpent,
  onDocument,
  onClose,
  galleryLink,
  resume,
}) => {
  const [phase, setPhase] = useState(
    /** @type {"loading" | "select" | "running" | "done" | "partial" | "failed" | "unknown" | "error"} */ (
      "loading"
    )
  );
  const [candidates, setCandidates] = useState(/** @type {TouchupCandidate[]} */ ([]));
  const [checked, setChecked] = useState(/** @type {Record<string, boolean>} */ ({}));
  const [loadError, setLoadError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmError, setConfirmError] = useState(/** @type {string | null} */ (null));
  const token = getToken();
  const ownerKey = `${uuid}:${token || ""}`;
  const [authorizedOwner, setAuthorizedOwner] = useState("");
  const paidRef = useRef(/** @type {any} */ (null));
  const handledReceiptsRef = useRef(new Set());
  const localSubmitRef = useRef(false);
  const [runSettlement, setRunSettlement] = useState(/** @type {any} */ (null));
  const [pollPaused, setPollPaused] = useState(false);
  const [needsSettlementCheck, setNeedsSettlementCheck] = useState(false);
  // The paywall, with the blocked selection riding its return stash so a
  // top-up comes back to the same photos.
  const [block, setBlock] = useState(
    /** @type {{ block: {balance: number, cost: number, action: string}, resume: { kind: "touchups", photos: string[] } } | null} */ (
      null
    )
  );
  const [creditsAdded, setCreditsAdded] = useState(
    /** @type {{ balance: number | null } | null} */ (null)
  );
  const resumeRef = useRef(resume || null);
  // Balance callbacks may change without invalidating the loaded photo selection.
  const onSpendOpenRef = useRef(onSpendOpen);
  onSpendOpenRef.current = onSpendOpen;
  const [job, setJob] = useState(/** @type {{id: string | null, count: number} | null} */ (null));
  const [galleryPath, setGalleryPath] = useState("");
  const [runNotice, setRunNotice] = useState("");
  const [runSummary, setRunSummary] = useState(/** @type {any} */ (null));
  const runGenerationRef = useRef(0);
  const activeUuidRef = useRef(uuid);
  activeUuidRef.current = uuid;
  const [loadRevision, setLoadRevision] = useState(0);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const manifest = /** @type {Record<string, string>} */ (doc?.photo_manifest || {});
  const listingUuid = typeof doc?.listing_uuid === "string" ? doc.listing_uuid : "";

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadRevision is the deliberate "Try again" trigger.
  useEffect(() => {
    let cancelled = false;
    const requestToken = token;
    setAuthorizedOwner("");
    setJob(null);
    setRunSummary(null);
    setRunSettlement(null);
    setConfirmOpen(false);
    setPollPaused(false);
    setPhase("loading");
    runGenerationRef.current += 1;
    setLoadError("");
    setChecked({});
    (async () => {
      try {
        const resp = await axios.get(`${API_URL}/computations/${uuid}/touchup-candidates/`, {
          headers: authHeader(),
          timeout: 15000,
        });
        if (cancelled || getToken() !== requestToken) return;
        setAuthorizedOwner(`${uuid}:${requestToken || ""}`);
        const loaded = normalizeCandidates(resp.data?.candidates);
        setCandidates(loaded);
        setPhase("select");
        // Once: the photos from before the top-up, re-checked, confirm open.
        const pending = resumeRef.current;
        resumeRef.current = null;
        if (pending) {
          const wanted = new Set(pending.photos);
          const again = loaded.filter((item) => wanted.has(item.photo));
          if (again.length > 0) {
            setChecked(Object.fromEntries(again.map((item) => [item.photo, true])));
            setCreditsAdded({ balance: pending.balance ?? null });
            setConfirmError(null);
            setConfirmOpen(true);
            if (onSpendOpenRef.current) void onSpendOpenRef.current();
          }
        }
      } catch (err) {
        if (cancelled || getToken() !== requestToken) return;
        const status = /** @type {{response?: {status?: number}}} */ (err).response?.status;
        setLoadError(
          status === 401 || status === 403
            ? "Sign in as the listing owner to choose photos."
            : "We couldn't load your photos. Check your connection, then try again."
        );
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uuid, token, loadRevision]);

  const selected = useMemo(
    () => candidates.filter((item) => checked[item.photo]),
    [candidates, checked]
  );
  const total = selected.reduce((sum, item) => sum + item.cost, 0);
  const buttonLabel = touchupButtonLabel(selected);

  const openConfirm = () => {
    if (selected.length === 0) return;
    setConfirmError(null);
    setConfirmOpen(true);
    if (onSpendOpen) void onSpendOpen();
  };

  /** A gallery may contain earlier results, so it cannot prove this job succeeded. */
  const finishRun = (
    /** @type {any} */ freshDoc,
    /** @type {any} */ finishedJob,
    /** @type {boolean} */ failed,
    expectedJobId = job?.id
  ) => {
    const summary = touchupRunSummary(finishedJob, expectedJobId);
    const path = freshDoc?.photo_touchups?.path;
    setGalleryPath(typeof path === "string" && path.startsWith("/touchups/") ? path : "");
    setRunSummary(summary);
    setPhase(
      summary
        ? summary.photos_failed === 0
          ? "done"
          : summary.photos_succeeded > 0
            ? "partial"
            : "failed"
        : failed
          ? "failed"
          : "unknown"
    );
    setJob(null);
  };

  const paid = usePaidAttempt({
    scope: { action: "touchups", targetUuid: uuid },
    enabled: !!token && authorizedOwner === ownerKey,
    onAccepted: (receipt) => {
      const acceptedJob = receipt.job;
      if (!acceptedJob || acceptedJob.job_id !== receipt.attempt.job_id)
        throw new Error("The exact touch-up job is unavailable.");
      const photos = receipt.attempt.request.photos;
      const id = receipt.attempt.attempt_id;
      if (!handledReceiptsRef.current.has(id)) {
        handledReceiptsRef.current.add(id);
        if (localSubmitRef.current && receipt.settlement.charged_credits > 0)
          track("credits_spent", {
            action: "photo_touchup",
            cost: receipt.settlement.charged_credits,
            balance: Number(receipt.attempt.response?.data?.balance ?? balance ?? 0),
          });
        if (onSpent) void onSpent();
      }
      localSubmitRef.current = false;
      setConfirmOpen(false);
      setConfirmError(null);
      setCreditsAdded(null);
      setRunSettlement(receipt.settlement);
      const terminal =
        receipt.replacement_chain_complete === true &&
        receipt.replacement_jobs.length === 0 &&
        ["completed", "failed"].includes(acceptedJob.status);
      if (terminal) {
        finishRun(doc, acceptedJob, acceptedJob.status === "failed", acceptedJob.job_id);
        if (paidMediaSettlementKnown(receipt.settlement)) paidRef.current?.acknowledge(id);
        const generation = runGenerationRef.current;
        const requestToken = getToken();
        void axios
          .get(`${API_URL}/computations/${uuid}/report-document/`, {
            headers: authHeader(),
            timeout: 15000,
          })
          .then((response) => {
            if (
              !mountedRef.current ||
              activeUuidRef.current !== uuid ||
              runGenerationRef.current !== generation ||
              getToken() !== requestToken
            )
              return;
            onDocument(response.data);
            const path = response.data?.photo_touchups?.path;
            setGalleryPath(typeof path === "string" && path.startsWith("/touchups/") ? path : "");
          })
          .catch(() => {});
      } else {
        setJob({ id: acceptedJob.job_id, count: photos.length });
        setPollPaused(false);
        setRunNotice(
          `Touching up ${photos.length} ${photos.length === 1 ? "photo" : "photos"} — usually ready within 15 minutes. You can leave this page; this updates by itself.`
        );
        setPhase("running");
      }
    },
    onRejected: (receipt) => {
      const response = receipt.attempt.response;
      const rejection = { response: { status: response?.status_code, data: response?.data } };
      const insufficient = insufficientCreditsFrom(rejection);
      setConfirmOpen(false);
      setCreditsAdded(null);
      localSubmitRef.current = false;
      if (insufficient) {
        setBlock({
          block: insufficient,
          resume: { kind: "touchups", photos: receipt.attempt.request.photos },
        });
      } else {
        const message = response?.data?.error;
        setConfirmError(
          typeof message === "string" && message.length <= 240
            ? message
            : "This touch-up request was not accepted."
        );
      }
      setPhase("select");
      paidRef.current?.acknowledge(receipt.attempt.attempt_id);
    },
  });
  paidRef.current = paid;
  const busy = paid.busy;
  const checkPaidAttempt = paid.check;

  // A fast terminal job read can beat the start POST's final microtask. Wait
  // for that single-flight operation to finish before asking for fresh ledger
  // settlement; joining the old POST would only repeat its queued snapshot.
  useEffect(() => {
    if (!needsSettlementCheck || busy) return;
    setNeedsSettlementCheck(false);
    void checkPaidAttempt();
  }, [needsSettlementCheck, busy, checkPaidAttempt]);

  const confirmTouchups = async () => {
    if (busy || selected.length === 0) return;
    runGenerationRef.current += 1;
    setRunSummary(null);
    setRunSettlement(null);
    setGalleryPath("");
    setConfirmError(null);
    localSubmitRef.current = true;
    await paid.submit({ photos: selected.map((item) => item.photo) });
  };

  usePollReportDocument({
    active: phase === "running" && job != null && !pollPaused && authorizedOwner === ownerKey,
    uuid,
    jobId: job?.id || null,
    isReady: () => true,
    onReady: (data, finishedJob) => {
      if (getToken() !== token || activeUuidRef.current !== uuid) return;
      onDocument(data);
      finishRun(data, finishedJob, false);
      setRunSettlement(null);
      setNeedsSettlementCheck(true);
    },
    onFailed: (_failure, failedJob) => {
      if (getToken() !== token || activeUuidRef.current !== uuid) return;
      const confirmedFailure = failedJob?.job_id === job?.id && failedJob?.status === "failed";
      finishRun(doc, failedJob, confirmedFailure);
      setRunSettlement(null);
      setNeedsSettlementCheck(true);
      if (!confirmedFailure) return;
      const generation = runGenerationRef.current;
      // Owner galleries include failure details even when no new photo succeeded.
      // This refresh does not change the run outcome or initiate another attempt.
      void axios
        .get(`${API_URL}/computations/${uuid}/report-document/`, {
          headers: authHeader(),
          timeout: 15000,
        })
        .then((response) => {
          if (
            !mountedRef.current ||
            activeUuidRef.current !== uuid ||
            getToken() !== token ||
            runGenerationRef.current !== generation
          )
            return;
          onDocument(response.data);
          const path = response.data?.photo_touchups?.path;
          setGalleryPath(typeof path === "string" && path.startsWith("/touchups/") ? path : "");
        })
        .catch(() => {});
    },
    onTimeout: () => {
      if (getToken() !== token || activeUuidRef.current !== uuid) return;
      setPollPaused(true);
      setRunNotice(
        "Your touch-ups are taking longer than usual. Check this request’s status before starting another run."
      );
    },
    maxMs: TOUCHUP_POLL_MAX_MS,
  });

  const tryAgain = () => {
    if (paid.pending) {
      void paid.check();
      return;
    }
    setPhase("loading");
    runGenerationRef.current += 1;
    setRunSummary(null);
    setJob(null);
    setGalleryPath("");
    setRunNotice("");
    setLoadRevision((n) => n + 1);
  };

  let body;
  if (phase === "loading") {
    body = (
      <Intro as="output" aria-live="polite">
        Looking over your photos…
      </Intro>
    );
  } else if (phase === "error") {
    body = (
      <>
        <Notice $kind="error" role="alert">
          {loadError}
        </Notice>
        <Actions>
          <SecondaryButton type="button" onClick={tryAgain}>
            Try again
          </SecondaryButton>
          <TextLink type="button" onClick={onClose}>
            Close
          </TextLink>
        </Actions>
      </>
    );
  } else if (phase === "running") {
    body = (
      <Notice as="output" aria-live="polite">
        {runNotice}
      </Notice>
    );
  } else if (["done", "partial", "failed", "unknown"].includes(phase)) {
    const failedResults =
      runSummary?.results?.filter((/** @type {any} */ result) => result.status === "failed") || [];
    const settlement = paidMediaSettlementMessage(runSettlement);
    body = (
      <>
        <Notice as="output" aria-live="polite">
          {phase === "done"
            ? "Your touched-up photos are ready."
            : phase === "partial"
              ? `${runSummary.photos_succeeded} of ${runSummary.photos_total} photos are ready; ${runSummary.photos_failed} could not be delivered.`
              : phase === "failed" && runSummary
                ? "No new photos were delivered by this run."
                : phase === "failed"
                  ? "That touch-up run did not finish successfully. Review available photos before retrying."
                  : "We couldn't verify this run's results. The gallery may include earlier photos."}
        </Notice>
        {["done", "partial"].includes(phase) && (
          <Notice>
            Compare every edited photo with its original before use. Automated checks are not a
            guarantee of accuracy.
          </Notice>
        )}
        {failedResults.length > 0 && (
          <ul>
            {failedResults.map((/** @type {any} */ result) => (
              <li key={result.photo_filename}>
                <strong>{cleanPhotoName(result.photo_filename)}:</strong>{" "}
                {touchupFailureMessage(result.failure_code)}
              </li>
            ))}
          </ul>
        )}
        {settlement && <Notice>{settlement}</Notice>}
        {galleryPath && galleryLink && (
          <Notice>
            {phase === "done" || phase === "partial"
              ? "Review the results: "
              : "Available photos and attempt details: "}
            {galleryLink(galleryPath)}
          </Notice>
        )}
        {phase === "unknown" && (
          <Notice>Refresh this page to check the latest results. No retry has been started.</Notice>
        )}
        <Actions>
          {phase !== "unknown" && !paid.pending && (
            <TextLink type="button" onClick={tryAgain}>
              {phase === "done" ? "Touch up more photos" : "Review retry options"}
            </TextLink>
          )}
          <TextLink type="button" onClick={onClose}>
            Close
          </TextLink>
        </Actions>
      </>
    );
  } else if (paid.pending) {
    body = null;
  } else if (candidates.length === 0) {
    body = (
      <>
        <Intro>The photo review didn't find anything to touch up in these photos.</Intro>
        <Actions style={{ marginTop: 10 }}>
          <TextLink type="button" onClick={onClose}>
            Close
          </TextLink>
        </Actions>
      </>
    );
  } else {
    const allChecked = selected.length === candidates.length;
    body = (
      <>
        <Head>
          <Intro>
            The photo review noted what could improve in each photo. Check the ones to touch up.
          </Intro>
          <TextLink
            type="button"
            onClick={() =>
              setChecked(
                allChecked ? {} : Object.fromEntries(candidates.map((item) => [item.photo, true]))
              )
            }
          >
            {allChecked ? "Clear" : "Select all"}
          </TextLink>
        </Head>
        <Grid>
          {candidates.map((item) => {
            const url = photoUrlFromManifest(manifest, item.photo);
            const isChecked = !!checked[item.photo];
            return (
              <PhotoChoice key={item.photo} $checked={isChecked}>
                <Thumb>{url ? <img src={url} alt="" /> : null}</Thumb>
                <ChoiceRow>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    aria-label={`Touch up ${cleanPhotoName(item.photo)}`}
                    onChange={(e) => {
                      // Read before the updater runs: React 16 pools the event.
                      const next = e.target.checked;
                      setChecked((prev) => ({ ...prev, [item.photo]: next }));
                    }}
                  />
                  <span>{cleanPhotoName(item.photo)}</span>
                </ChoiceRow>
                <Chips>
                  {item.reasons.map((reason) => (
                    <Chip key={reason}>{reasonLabel(reason)}</Chip>
                  ))}
                  {item.already_touched && (
                    <Chip>{item.cost === 0 ? "Redo · your free redo" : "Redo"}</Chip>
                  )}
                </Chips>
              </PhotoChoice>
            );
          })}
        </Grid>
        <Actions>
          <PrimaryButton type="button" disabled={selected.length === 0} onClick={openConfirm}>
            {buttonLabel}
          </PrimaryButton>
          <TextLink type="button" onClick={onClose}>
            Not now
          </TextLink>
        </Actions>
      </>
    );
  }

  return (
    <Panel data-testid="touchup-picker">
      {paid.pending && (
        <>
          <Notice as="output" aria-live="polite">
            {paid.receipt?.attempt.status === "accepted"
              ? "This request was accepted. Checking its progress does not start another touch-up."
              : "We haven’t confirmed this request’s outcome yet. Check its status before starting another touch-up."}
          </Notice>
          <Actions>
            <SecondaryButton type="button" disabled={busy} onClick={() => void paid.check()}>
              {busy ? "Checking status…" : "Check status"}
            </SecondaryButton>
          </Actions>
        </>
      )}
      {paid.error && (
        <Notice $kind="error" role="alert">
          {paid.error}
        </Notice>
      )}
      {!confirmOpen && confirmError && (
        <Notice $kind="error" role="alert">
          {confirmError}
        </Notice>
      )}
      {body}
      {confirmOpen && !paid.pending && (
        <ConfirmSpendSheet
          title={buttonLabel}
          cost={total}
          balance={balance}
          note={
            creditsAdded
              ? (() => {
                  const known = balance ?? creditsAdded.balance;
                  return known === null || known === undefined
                    ? "Credits added."
                    : `Credits added — you have ${formatCreditsNumber(known)}.`;
                })()
              : "Usually ready within 15 minutes. Every new edit needs an automated check; compare delivered photos with the originals before use."
          }
          busy={busy}
          error={confirmError}
          confirmLabel="Touch up"
          onConfirm={confirmTouchups}
          onClose={() => {
            if (busy) return;
            setConfirmOpen(false);
            setCreditsAdded(null);
          }}
        />
      )}
      {block && (
        <CreditsPaywallSheet
          block={block.block}
          resume={block.resume}
          onClose={() => setBlock(null)}
          listingUuid={listingUuid || undefined}
        />
      )}
    </Panel>
  );
};

export default TouchupPicker;
